import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscape } from '../../lib/hooks';
import { IconButton } from './Button';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Content width as written in the design (e.g. 520, 600). */
  width?: number;
  children: ReactNode;
  /** Hide the header (e.g. for success states with their own layout). */
  bare?: boolean;
}

/** Widths in the design are the content box; the card adds 28px padding on each side. */
const CARD_PADDING = 56;

const FOCUSABLE = 'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, description, width = 520, children, bare }: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEscape(onClose, open);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const first = cardRef.current?.querySelector<HTMLElement>('input, textarea') ?? cardRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={cardRef} className={styles.card} style={{ width: width + CARD_PADDING }} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        {!bare && (title || description) ? (
          <div className={styles.header}>
            <div className={styles.titles}>
              {title ? <h2 className={styles.title}>{title}</h2> : null}
              {description ? <p className={styles.description}>{description}</p> : null}
            </div>
            <IconButton label="Закрыть" onClick={onClose}>
              <X size={18} strokeWidth={1.8} />
            </IconButton>
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ModalActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}
